import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import {
  useTable,
  useSortBy,
  useExpanded,
  useColumnOrder,
  usePagination,
  useRowSelect,
  useGroupBy,
} from "react-table";
import PropTypes from "prop-types";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import AddNewItemRow from "./AddNewItemRow";
import SubTotalRow from "./SubTotalRow";
import { SendMessage } from "../../components/SendMessage";
import "./index.css";
import "../NewProductCategories/ProductDetailsLCA/components/LovableFlowControls/index.css";
import { TableVariantContext } from "./TableVariantContext";
import ErrorRow from "./ErrorRow";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  Assessment,
  CloudUpload,
} from "@mui/icons-material";
import { useExcelExport } from "../../hooks/useExcelExport";
import EFRAGService from "../../apis/EFRAG/efragService";
import exportIcon from "../../assets/design-revamp-company/export-excel/export_icon.svg";
import { LovableExportExcelButton } from "../NewProductCategories/ProductDetailsLCA/components/LovableFlowControls";
import {
  useLcaRowComparison,
  LcaComparisonDialog,
  TableComparisonProvider,
  defaultComparisonItemLabel,
  resolveComparisonCategories,
} from "../NewProductCategories/ProductDetailsLCA/components/lcaComparison";
import { Diamond } from "lucide-react";

const CustomTable = React.memo(
  ({
    columns,
    data,
    setData,
    onAdd,
    onEdit,
    onDelete,
    subtotalColumns,
    customInputs,
    dropdownData,
    hiddenColumns,
    toggleColumnVisibility,
    disableRules,
    dynamicPlaceholders,
    noAddNewRow = false,
    initialPageSize = 10,
    pageSizeOptions = [5, 10, 20, 30, 40, 50],
    enableGrouping = false,
    enablePagination = false,
    groupBy = [],
    noShow,
    enableExport = true,
    enableEFRAGIntegration = false,
    reportMetadata = {},
    onNewItemFormReset,
    variant,
    tableClassName = "",
    highlightedRowId,
    comparisonSelectedIds: comparisonSelectedIdsProp,
    onToggleCompare: onToggleCompareProp,
    comparisonMaxSelected = 5,
    comparisonConfig,
  }) => {
    const wrapperRef = useRef(null);
    const [resolvedVariant, setResolvedVariant] = useState(variant || "classic");
    const internalComparison = useLcaRowComparison(comparisonMaxSelected);

    const resolveVariant = useCallback(() => {
      if (variant) return variant;
      const root = wrapperRef.current;
      if (!root) return "classic";
      const lovableScopes = [".azilis-detail-flows", ".company-ghg-yfs"];
      return lovableScopes.some((selector) => root.closest(selector))
        ? "lovable"
        : "classic";
    }, [variant]);

    useLayoutEffect(() => {
      setResolvedVariant(resolveVariant());
    }, [resolveVariant, data, columns]);

    const isCompanyGhg = useCallback(() => {
      const root = wrapperRef.current;
      return !!root?.closest(".company-ghg-yfs");
    }, []);

    const comparisonEnabled =
      resolvedVariant === "lovable" && comparisonConfig !== false;

    const comparisonOwnedInternally =
      comparisonEnabled && typeof onToggleCompareProp !== "function";

    const comparisonSelectedIds = comparisonOwnedInternally
      ? internalComparison.selectedIds
      : comparisonSelectedIdsProp;

    const onToggleCompare = comparisonOwnedInternally
      ? internalComparison.toggle
      : onToggleCompareProp;

    const openComparisonDialog = comparisonOwnedInternally
      ? internalComparison.openDialog
      : undefined;

    const comparisonDialogOpen = comparisonOwnedInternally
      ? internalComparison.dialogOpen
      : false;

    const setComparisonDialogOpen = comparisonOwnedInternally
      ? internalComparison.setDialogOpen
      : undefined;

    const resolvedComparisonCategories = useMemo(() => {
      const configured =
        comparisonConfig && typeof comparisonConfig === "object"
          ? comparisonConfig.categories
          : undefined;
      return resolveComparisonCategories(configured, data);
    }, [comparisonConfig, data]);

    const comparisonGetItemLabel = useMemo(() => {
      if (comparisonConfig && typeof comparisonConfig === "object") {
        if (typeof comparisonConfig.getItemLabel === "function") {
          return comparisonConfig.getItemLabel;
        }
      }
      return defaultComparisonItemLabel;
    }, [comparisonConfig]);

    const comparisonTitle = useMemo(() => {
      if (comparisonConfig && typeof comparisonConfig === "object" && comparisonConfig.title) {
        return comparisonConfig.title;
      }
      return isCompanyGhg() ? "GHG Sammenligning" : "LCA Sammenligning";
    }, [comparisonConfig, isCompanyGhg]);

    const comparisonContextValue = useMemo(() => {
      if (!comparisonEnabled) return null;
      return {
        enabled: true,
        selectedIds: comparisonSelectedIds || [],
        toggle: onToggleCompare,
        openDialog: openComparisonDialog || (() => {}),
        maxSelected: comparisonMaxSelected,
        categories: resolvedComparisonCategories,
      };
    }, [
      comparisonEnabled,
      comparisonSelectedIds,
      onToggleCompare,
      openComparisonDialog,
      comparisonMaxSelected,
      resolvedComparisonCategories,
    ]);

    const [errors, setErrors] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editedData, setEditedData] = useState({});
    const [open, setOpen] = useState(false);
    const [newRows, setNewRows] = useState([]);
    const [efragStatus, setEfragStatus] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const { exportToExcel, generateEFRAGTemplate } = useExcelExport();

    const tableColumns = useMemo(() => {
      const baseColumns = [
        ...columns.map((col) => {
          const column = {
            ...col,
            isVisible: !hiddenColumns.includes(col.accessor),
            aggregate: col.type === "number" ? "sum" : "unique",
            Aggregated: ({ value }) => {
              if (typeof value === "number") {
                return `Total: ${value.toFixed(2)}`;
              }
              return `${value}`;
            },
          };
          if (col.unit) {
            const unitInfo =
              'Some selected items do not have units; "unit" will be shown as a placeholder.';
            if (typeof column.tipBody === "string") {
              if (!column.tipBody.includes('"unit" will be shown as a placeholder')) {
                column.tipHeader = column.tipHeader || "Tip";
                column.tipBody = `${column.tipBody} ${unitInfo}`;
              }
            } else if (!column.tipBody) {
              column.tipHeader = column.tipHeader || "Tip";
              column.tipBody = unitInfo;
            }
          }
          // Convert lowercase header to Header for react-table
          if (col.header !== undefined && col.Header === undefined) {
            column.Header = col.header;
          }
          // Convert lowercase cell to Cell for react-table
          if (col.cell !== undefined && col.Cell === undefined) {
            column.Cell = col.cell;
          }
          // Convert canSort to disableSortBy for react-table (inverted logic)
          // Keep canSort for our custom UI logic in TableHeader
          if (col.hasOwnProperty('canSort')) {
            column.disableSortBy = !col.canSort;
          }
          return column;
        }),
      ];

      // Only add expander column if grouping is enabled
      if (enableGrouping) {
        return [
          {
            id: "expander",
            Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
              <span {...getToggleAllRowsExpandedProps()}>
                {isAllRowsExpanded ? (
                  <KeyboardArrowDown className="expand-icon" />
                ) : (
                  <KeyboardArrowDown className="expand-icon" />
                )}
              </span>
            ),
            Cell: ({ row }) =>
              row.canExpand ? (
                <span
                  {...row.getToggleRowExpandedProps({
                    style: {
                      paddingLeft: `${row.depth * 2}rem`,
                    },
                  })}
                >
                  {row.isExpanded ? (
                    <KeyboardArrowDown className="expand-icon" />
                  ) : (
                    <KeyboardArrowRight className="expand-icon" />
                  )}
                </span>
              ) : null,
            width: 50,
          },
          ...baseColumns,
          {
            id: "actions",
            header: "",
            Cell: ({ row }) => null,
            width: comparisonEnabled ? 130 : 80,
          },
        ];
      }

      return [
        ...baseColumns,
        {
          id: "actions",
          header: "",
          Cell: ({ row }) => null,
          width: comparisonEnabled ? 130 : 80,
        },
      ];
    }, [columns, hiddenColumns, enableGrouping, comparisonEnabled]);

    const fullTableData = useMemo(() => [...data, ...newRows], [data, newRows]);
    const autoShowFirstAddRow = fullTableData.length === 0;

    const tableInstance = useTable(
      {
        columns: tableColumns,
        data: fullTableData,
        initialState: {
          pageSize: initialPageSize,
          ...(enableGrouping && { groupBy: groupBy }),
        },
      },
      useColumnOrder,
      ...(enableGrouping ? [useGroupBy] : []),
      useSortBy,
      useExpanded,
      ...(enablePagination ? [usePagination] : []),
      useRowSelect
    );

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      rows,
      prepareRow,
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize },
      toggleGroupBy,
      toggleAllRowsExpanded,
      // allColumns,
    } = tableInstance;

    const rowsToRender = enablePagination ? page : rows;

    // Export functions
    const handleExportToExcel = useCallback(async () => {
      try {
        setIsExporting(true);
        const filename = `${reportMetadata.entityName || "table"}-export-${
          new Date().toISOString().split("T")[0]
        }`;
        await exportToExcel(fullTableData, columns, filename);
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [fullTableData, columns, exportToExcel, reportMetadata]);

    const handleEFRAGExport = useCallback(async () => {
      try {
        setIsExporting(true);
        const efragBlob = await generateEFRAGTemplate(fullTableData, columns);
        const filename = `${reportMetadata.entityName || "efrag"}-template-${
          new Date().toISOString().split("T")[0]
        }.xlsx`;

        // Download the EFRAG template
        const url = window.URL.createObjectURL(efragBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        if (enableEFRAGIntegration) {
          // Upload to EFRAG converter
          const uploadResult = await EFRAGService.uploadToEFRAGConverter(
            efragBlob
          );
          setEfragStatus({ status: "uploaded", fileId: uploadResult.fileId });
        }
      } catch (error) {
        console.error("EFRAG export failed:", error);
        alert("EFRAG export failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [
      fullTableData,
      columns,
      generateEFRAGTemplate,
      reportMetadata,
      enableEFRAGIntegration,
    ]);

    const handleEFRAGConversion = useCallback(async () => {
      if (!efragStatus?.fileId) return;

      try {
        setIsExporting(true);
        const conversionResult = await EFRAGService.processEFRAGConversion(
          efragStatus.fileId
        );
        setEfragStatus({
          ...efragStatus,
          status: "converting",
          conversionId: conversionResult.conversionId,
        });

        // Poll for conversion status
        const checkStatus = async () => {
          const status = await EFRAGService.getConversionStatus(
            conversionResult.conversionId
          );
          setEfragStatus((prev) => ({ ...prev, ...status }));

          if (status.status === "completed") {
            const reportBlob = await EFRAGService.downloadConvertedReport(
              conversionResult.conversionId
            );
            const url = window.URL.createObjectURL(reportBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `efrag-report-${
              new Date().toISOString().split("T")[0]
            }.xbrl`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } else if (status.status === "failed") {
            alert(
              "EFRAG conversion failed. Please check your data and try again."
            );
          } else {
            setTimeout(checkStatus, 2000); // Poll every 2 seconds
          }
        };

        setTimeout(checkStatus, 1000);
      } catch (error) {
        console.error("EFRAG conversion failed:", error);
        alert("EFRAG conversion failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [efragStatus]);

    const renderGroupHeader = useCallback((row) => {
      const groupValue = row.values[row.groupByID];
      return (
        <span>
          {row.groupByID}: {groupValue} ({row.subRows.length} items)
        </span>
      );
    }, []);

    const handleAddNewItem = useCallback((newItem) => {
      setNewRows((prev) => [...prev, newItem]);
    }, []);

    const handleInputChange = useCallback((rowIndex, columnId, value) => {
      setEditedData((prev) => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [columnId]: value,
        },
      }));
    }, []);

    const handleValidation = useCallback(
      (newData) => {
        const newErrors = {};
        columns.forEach((column) => {
          const { accessor, type, optional, errorText } = column;
          const value = newData[accessor];

          if (!optional && !value) {
            newErrors[accessor] = errorText || "This field is required";
          }
          if (value) {
            if (type === "number" && isNaN(Number(value))) {
              newErrors[accessor] = errorText || "Invalid number";
            } else if (type === "text" && typeof value !== "string") {
              newErrors[accessor] = errorText || "Invalid text";
            }
          }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      [columns]
    );

    const handleSave = useCallback(
      (rowIndex) => {
        const newData = editedData[rowIndex] || {};
        const newErrors = handleValidation(newData);

        if (Object.keys(newErrors).length === 0) {
          // rowIndex is row.index (index in fullTableData), NOT rows[]. position — sorting
          // would otherwise attach edits to the wrong row (switches reverting, etc.).
          const row =
            rows.find((r) => r.index === rowIndex) ??
            (rowIndex >= 0 && rowIndex < fullTableData.length
              ? { index: rowIndex, original: fullTableData[rowIndex] }
              : null);
          if (!row) return;
          const isNewRow = row.index >= data.length;
          const rowData = { ...row.original, ...newData };

          if (isNewRow) {
            setNewRows((prev) =>
              prev.map((r, i) => (i === row.index - data.length ? rowData : r))
            );
            onAdd(rowData);
          } else {
            setData((prev) =>
              prev.map((r, i) => (i === row.index ? rowData : r))
            );
            onEdit(rowData);
          }

          setEditedData((prev) => {
            const { [rowIndex]: removed, ...rest } = prev;
            return rest;
          });
          setEditingRow(null);
          setErrors({});
        } else {
          setErrors(newErrors);
        }
      },
      [
        editedData,
        rows,
        data.length,
        fullTableData,
        onAdd,
        onEdit,
        setData,
        handleValidation,
      ]
    );

    const handleDelete = useCallback(
      (rowIndex) => {
        return onDelete(rowIndex);
      },
      [onDelete]
    );

    const handleStartEditing = useCallback(
      (rowIndex) => {
        setEditingRow(rowIndex);
        setEditedData((prev) => ({
          ...prev,
          [rowIndex]: { ...data[rowIndex] },
        }));
      },
      [data]
    );

    const handleCancelEdit = useCallback((rowIndex) => {
      setEditingRow(null);
      setEditedData((prev) => {
        const newData = { ...prev };
        delete newData[rowIndex];
        return newData;
      });
    }, []);

    const renderExportToolbar = () => {
      const isLovable = resolvedVariant === "lovable";
      const toolbarClass = isLovable ? "lovable-export-toolbar" : "export-toolbar";
      const selectedCount = comparisonSelectedIds?.length || 0;

      return (
        <div className={toolbarClass}>
          {comparisonEnabled && selectedCount > 0 && (
            <button
              type="button"
              className={
                isLovable
                  ? "lovable-secondary-btn"
                  : "export-button"
              }
              onClick={() => {
                if (openComparisonDialog) openComparisonDialog();
                else if (setComparisonDialogOpen) setComparisonDialogOpen(true);
              }}
              title={`Compare ${selectedCount} selected rows`}
            >
              <Diamond className="lovable-secondary-btn__icon" strokeWidth={2.5} />
              <span>Compare ({selectedCount})</span>
            </button>
          )}
          {enableExport &&
            (isLovable ? (
              <LovableExportExcelButton
                onClick={handleExportToExcel}
                isExporting={isExporting}
              />
            ) : (
              <button
                onClick={handleExportToExcel}
                disabled={isExporting}
                className="export-button excel-export"
              >
                <img src={exportIcon} alt="Export icon" width={18} height={18} />
                {isExporting ? "Exporting..." : "Export to Excel"}
              </button>
            ))}

          {enableEFRAGIntegration && (
            <>
              <button
                onClick={handleEFRAGExport}
                disabled={isExporting}
                className={
                  isLovable
                    ? "lovable-secondary-btn lovable-secondary-btn--efrag"
                    : "export-button efrag-export"
                }
              >
                <Assessment
                  className={
                    isLovable ? "lovable-secondary-btn__icon" : "export-icon"
                  }
                />
                {isExporting ? "Generating..." : "Generate EFRAG Template"}
              </button>

              {efragStatus?.fileId && (
                <button
                  onClick={handleEFRAGConversion}
                  disabled={isExporting || efragStatus.status === "converting"}
                  className={
                    isLovable
                      ? "lovable-secondary-btn lovable-secondary-btn--efrag"
                      : "export-button efrag-convert"
                  }
                >
                  <CloudUpload
                    className={
                      isLovable ? "lovable-secondary-btn__icon" : "export-icon"
                    }
                  />
                  {efragStatus.status === "converting"
                    ? "Converting..."
                    : "Convert to XBRL"}
                </button>
              )}
            </>
          )}
        </div>
      );
    };

    const renderPagination = () => (
      <div className="pagination-container">
        <div className="pagination-buttons">
          <button
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {"<<"}
          </button>
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {"<"}
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="pagination-button"
          >
            {">"}
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            className="pagination-button"
          >
            {">>"}
          </button>
        </div>
        <span className="pagination-info">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="pagination-select"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    );
    const tableWrapperClass = [
      "custom-table",
      "custom-table-wrapper",
      resolvedVariant === "lovable" ? "custom-table--lovable" : "",
      tableClassName,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <TableVariantContext.Provider value={resolvedVariant}>
      <TableComparisonProvider value={comparisonContextValue}>
      <div ref={wrapperRef} className={tableWrapperClass}>
        {(enableExport || enableEFRAGIntegration || (comparisonEnabled && (comparisonSelectedIds?.length || 0) > 0)) &&
          renderExportToolbar()}

        {efragStatus && (
          <div className="efrag-status">
            Status: {efragStatus.status}
            {efragStatus.message && ` - ${efragStatus.message}`}
          </div>
        )}
        <div className="custom-table-scroll">
        <table
          {...getTableProps()}
          className="custom-table-element"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <TableHeader
            headerGroups={headerGroups}
            toggleColumnVisibility={toggleColumnVisibility}
            hiddenColumns={hiddenColumns}
            toggleGroupBy={enableGrouping ? toggleGroupBy : undefined}
          />
          <TableBody
            getTableBodyProps={getTableBodyProps}
            rows={rowsToRender}
            prepareRow={prepareRow}
            customInputs={customInputs}
            dropdownData={dropdownData}
            onInputChange={handleInputChange}
            hiddenColumns={hiddenColumns}
            editingRow={editingRow}
            editedData={editedData}
            onStartEditing={handleStartEditing}
            onSave={handleSave}
            onCancelEdit={handleCancelEdit}
            onDelete={handleDelete}
            setOpen={setOpen}
            disableRules={disableRules}
            noShow={noShow}
            dynamicPlaceholders={dynamicPlaceholders}
            renderGroupHeader={enableGrouping ? renderGroupHeader : undefined}
            highlightedRowId={highlightedRowId}
            comparisonSelectedIds={comparisonEnabled ? comparisonSelectedIds : undefined}
            onToggleCompare={comparisonEnabled ? onToggleCompare : undefined}
            comparisonMaxSelected={comparisonMaxSelected}
            afterRows={
              !noAddNewRow ? (
                <>
                  {newRows.map((_, index) => (
                    <AddNewItemRow
                      key={index}
                      columns={columns}
                      onAdd={onAdd}
                      onAddRow={handleAddNewItem}
                      customInputs={customInputs}
                      dropdownData={dropdownData}
                      hiddenColumns={hiddenColumns}
                      handleValidation={handleValidation}
                      disableRules={disableRules}
                      noShow={noShow}
                      dynamicPlaceholders={dynamicPlaceholders}
                      newRows={newRows}
                      setOpen={setOpen}
                      onNewItemFormReset={onNewItemFormReset}
                    />
                  ))}
                  <AddNewItemRow
                    columns={columns}
                    onAdd={onAdd}
                    onAddRow={handleAddNewItem}
                    customInputs={customInputs}
                    dropdownData={dropdownData}
                    hiddenColumns={hiddenColumns}
                    handleValidation={handleValidation}
                    disableRules={disableRules}
                    noShow={noShow}
                    dynamicPlaceholders={dynamicPlaceholders}
                    setOpen={setOpen}
                    onNewItemFormReset={onNewItemFormReset}
                    autoShow={autoShowFirstAddRow}
                  />
                </>
              ) : null
            }
          />
          {!noAddNewRow && <ErrorRow columns={columns} errors={errors} />}
          {!noAddNewRow && (
            <SubTotalRow
              columns={columns}
              subtotalColumns={subtotalColumns}
              data={fullTableData}
              hiddenColumns={hiddenColumns}
            />
          )}
        </table>
        </div>
        {enablePagination && renderPagination()}
        {open && (
          <div
            className="modal-overlay"
            style={{ zIndex: 999999, backgroundColor: "#FFFFFF" }}
          >
            <SendMessage setOpen={setOpen} />
          </div>
        )}
        {comparisonOwnedInternally && (
          <LcaComparisonDialog
            open={comparisonDialogOpen}
            onClose={() => setComparisonDialogOpen(false)}
            title={comparisonTitle}
            impactCategories={resolvedComparisonCategories}
            selectedIds={comparisonSelectedIds}
            items={data}
            getItemLabel={comparisonGetItemLabel}
            maxSelected={comparisonMaxSelected}
          />
        )}
      </div>
      </TableComparisonProvider>
      </TableVariantContext.Provider>
    );
  }
);

CustomTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      type: PropTypes.oneOf([
        "text",
        "number",
        "dropdown",
        "switch",
        "custom",
        "spacer",
        "function",
        "multipleCustom",
        "indicator",
      ]).isRequired,
      optional: PropTypes.bool,
      canSort: PropTypes.bool,
      choice1: PropTypes.string,
      choice2: PropTypes.string,
      styleNo: PropTypes.number,
      canHide: PropTypes.bool,
      tipHeader: PropTypes.string,
      tipBody: PropTypes.string,
      width: PropTypes.string,
      subtotalFixed: PropTypes.bool,
      subtotalUnit: PropTypes.string,
      unit: PropTypes.string,
      minValue: PropTypes.number,
      maxValue: PropTypes.number,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  subtotalColumns: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ),
  customInputs: PropTypes.objectOf(PropTypes.func),
  dropdownData: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  initialPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  groupBy: PropTypes.arrayOf(PropTypes.string),
  enableExport: PropTypes.bool,
  enableEFRAGIntegration: PropTypes.bool,
  reportMetadata: PropTypes.object,
};

export default CustomTable;
